import { Component, AfterViewInit, OnDestroy, OnInit, ViewEncapsulation, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformServer, Location } from '@angular/common';
import { MetaDefinition } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { City } from '../models/city.model';
import { AboutCity } from '../models/about-city.model';
import { UsefulInformation } from '../models/useful-information.model';
import { CityService } from '../services/city.service';
import { LocationService } from '../services/location.service';
import { ProductService } from '../services/product.service';
import { CurrencyService } from '../shared/currency.service';
import { AppSettings } from '../settings/app.settings';
import { AccentsUtils } from '../shared/accents-utils.service'
import { HrefLang, HrefLangDefinition } from '../shared/hreflang.service';
import { CheckingUrl } from '../shared/checking-url.service';
import { DeviceService } from '../shared/device.service';
import { SEOService } from '../shared/seo-service';
import { Category } from './filter-bar/filter-bar.component';

@Component({
  selector: 'city',
  templateUrl: './city.component.html',
  styleUrls: ['./city.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class CityComponent implements OnInit, AfterViewInit, OnDestroy {

  public isNode: boolean = false;
  public id: number;
  public city: City;
  public abouts: AboutCity[];
  public useful: UsefulInformation[];
  public topProducts: Object[];
  public filteredProducts: Object[] = [];
  public allProducts: Object[] = [];
  public product: any;
  public hasNext: boolean = true;
  public topProductsAnyLocation: any[];
  public topLocations: any[];
  public cookieCity: string;
  public lstCategories: Category[];
  private selectedCategories: Number[] = [];
  private filterText: String = "";
  public isDesktop: boolean = false;

  public queryParams: Object = { offset: 4, size: 20 };

  private subscription: Subscription = new Subscription();

  constructor(private cityService: CityService, private productService: ProductService, private location: Location,
    private locationService: LocationService, private route: ActivatedRoute, private router: Router,
    private translate: TranslateService, @Inject(PLATFORM_ID) private platformId: string,
    public currencyService: CurrencyService, private hlang: HrefLang, private seoService: SEOService,
    private accentsUtils: AccentsUtils, private checkUrl: CheckingUrl, private deviceService: DeviceService) { };

  ngOnInit() {
    this.isNode = isPlatformServer(this.platformId);

    this.route.params.subscribe(params => {
      if (isNaN(parseInt(params['id']))) {
        this.router.navigate(['/oops/400']);
        throw new Error("400");
      } else {
        this.id = +params['id'];
        this.city = new City();
        this.city.location = params['location'];
        this.initFunction(this.id)
      }
    });
    this.applyFilters();
  }

  public ngAfterViewInit() {
    this.subscription.add(this.currencyService.currencyUpdated.subscribe(currency => {
      if (this.id != null && !isNaN(this.id)) {
        this.findAllProductsByCity(this.id);
      }
    }));
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private initFunction(id) {
    this.allProducts = [];
    this.getCity(id);
    this.getCityHrefs(id);
    if (this.deviceService.device == "mobile") {
      this.queryParams = { page: 0, size: 50 };
      this.isDesktop = false;
    } else {
      this.queryParams = { page: 0, size: 50 };
      this.isDesktop = true;
    }
    this.findAllProductsByCity(id);
    this.getAboutCity(id);
  }

  private findAllProductsByCity(id: number) {
    this.productService.findAllByCity(id, this.queryParams).subscribe(products => {
      let _products;
      if (products.length == 0) {
        _products = []
      } else {
        _products = JSON.parse(JSON.stringify(products));
      }
      this.hasNext = _products['hasNext'];
      _products = _products['content'].map(function (product) {
        product.micro = encodeURI(`${this.deviceService.constructCloudfrontURL(AppSettings.CSS_CE_IMAGES, 'MICRO')}/${product.main}`);
        product.main = encodeURI(`${this.deviceService.constructCloudfrontURL(AppSettings.CSS_CE_IMAGES, 'SMALL')}/${product.main}`);
        return product;
      }.bind(this));
      if (products['offset'] == 0) {
        this.allProducts = _products;
      } else {
        this.allProducts = this.allProducts.concat(_products);
      }
      this.calculeHighPrice(this.allProducts);
      this.lstCategories = this.extractCategories(this.allProducts);
      if (this.deviceService.device == "mobile") {
        this.listForMobile();
      } else {
        this.applyFilters();
      }
    });
  }

  private calculeHighPrice(products) {
    products.map(opt => {
      if (opt.bestPricePromotion && opt.bestPricePromotion.promotionType === 'RETAIL') {
        opt.highPrice = (opt.from * 100) / (100 - opt.bestPricePromotion.value);
      }
    });
  }

  private getCity(id: number) {
    this.cityService.getCity(id).subscribe(city => {
      this.findTopLocations(city.continent);
      this.findTopProducts(city.continent);
      let _city = JSON.parse(JSON.stringify(city));
      _city.headerMicro = `${this.deviceService.constructCloudfrontURL(AppSettings.LOCATION_IMAGES, 'MICRO')}/${_city.header}`;
      if (this.deviceService.device == "mobile") {
        _city.header = `${this.deviceService.constructCloudfrontURL(AppSettings.LOCATION_IMAGES, 'MEDIUM')}/${_city.header}`;
      } else {
        _city.header = `${AppSettings.LOCATION_IMAGES}${_city.header}`;
      }
      _city.aboutMicroImage = `${this.deviceService.constructCloudfrontURL(AppSettings.LOCATION_IMAGES, 'MICRO')}/${_city.aboutImage}`;
      _city.aboutImage = `${this.deviceService.constructCloudfrontURL(AppSettings.LOCATION_IMAGES, 'LARGE')}/${_city.aboutImage}`;

      this.translate.get("META_TITLE_DEFAULT_CITY", { value: _city.location }).subscribe(text => {
        this.seoService.updateTitle(text);
      });
      //this.seoService.updateTitle(`Descubre ${_city.location} | City Expert Información Turística`);
      this.translate.get("META_DESC_DEFAULT_CITY", { value: _city.location }).subscribe(text => {
        let tag: MetaDefinition = {
          name: "description",
          content: text
        };
        this.seoService.updateDescription(tag);
      });

      this.city = _city;
    });
  }

  private getAboutCity(id: number) {
    this.cityService.getAboutCity(id).subscribe(abouts => {
      let _abouts = JSON.parse(JSON.stringify(abouts));
      this.abouts = _abouts.map(function (about) {
        about.imageMicro = `${this.deviceService.constructCloudfrontURL(AppSettings.LOCATION_IMAGES, 'MICRO')}/${about.image}`;
        if (this.deviceService.device == "mobile") {
          about.image = `${this.deviceService.constructCloudfrontURL(AppSettings.LOCATION_IMAGES, 'SMALL')}/${about.image}`;
        } else {
          about.image = `${this.deviceService.constructCloudfrontURL(AppSettings.LOCATION_IMAGES, 'LARGE')}/${about.image}`;
        }
        return about;
      }.bind(this));
    });
  }

  public loadMore = function () {
    // this.queryParams['page']++;
    // this.findAllProductsByCity(this.city.id);
    this.filteredProducts = this.allProducts;
    this.applyFilters();
    this.hasNext = false;
  }

  getCityHrefs(id: number) {
    let _hlangs: HrefLangDefinition[] = [];
    this.cityService.getCityHrefs(id).subscribe((hrefs) => {
      hrefs.map(href => {
        let hrefLocale = href.locale.split("_")[0];
        let cityUrl = href.url;

        let hreflang_: HrefLangDefinition = {
          rel: "alternate",
          href: `${AppSettings.BASE_DOMAIN}/${hrefLocale}/${id}/${cityUrl}`,
          hreflang: `${hrefLocale}`
        }
        _hlangs.push(hreflang_);
        this.hlang.addTags(hreflang_);
      });
      let currentUrl = this.router.url.split('?')[0];
      let res = this.checkUrl.compareUrl(_hlangs, this.router.url.split('?')[0]);
      let canonicalUrl = currentUrl;
      if (res != null) {
        canonicalUrl = res.url;
        if (this.isNode)
          Zone.current.get('res').redirect(301, res.url);
        else {
          const urlPart = res.url.split("/");
          const url = this.router.createUrlTree([`/${urlPart[3]}`, urlPart[4], urlPart[5]], { relativeTo: this.route }).toString();
          this.location.go(url);
          // window.location.href = res.url;
        }
      }
      this.seoService.createLinkForCanonicalURL(canonicalUrl);
    });
  }

  scrollIntoView(eleID) {
    var e = document.getElementById(eleID);
    if (!!e && e.scrollIntoView) {
      e.scrollIntoView(true);
      var scrolledY = window.scrollY;
      if (scrolledY) {
        window.scroll(0, scrolledY - AppSettings.TOP_PADDING);
      }
    }
  }

  private findTopLocations(continentId: number) {
    this.locationService.findTopLocations(continentId).subscribe(locations => {
      let _locations = JSON.parse(JSON.stringify(locations));
      this.topLocations = _locations.map(l => {
        return { url: `/${l.lang}/${l.id}/${this.accentsUtils.transformUrl(l.location)}`, name: l.location }
      });
    });
  }

  private findTopProducts(continentId: number) {
    this.productService.findTopProducts(continentId).subscribe(products => {
      let _products = JSON.parse(JSON.stringify(products));
      this.topProductsAnyLocation = _products.map(l => {
        let language = l.textLanguage.locale.split("_")[0];
        let hrefLang = l.hreflangs[0];
        if (l.hreflangs.length > 1) {
          hrefLang = l.hreflangs.find(hl => {
            return hl.locale.split("_")[0] == language.split("_")[0];
          });
        }
        let locationId = hrefLang.locationId;
        let locationName = hrefLang.locationName;
        let name = hrefLang.url;
        let thumbnail = `${this.deviceService.constructCloudfrontURL(AppSettings.CSS_CE_IMAGES, 'SMALL')}/${l.thumbnail}`;
        return { url: `/${language}/${locationId}/${locationName}/${l.id}/${name}`, name: l.name, thumbnail: thumbnail }
      });
    });
  }

  /**               
   * Extracts all the categories from the listed products.
   */
  private extractCategories(products: any[]): Category[] {
    let result: Category[] = [];
    let names = [];

    products.map(product => {
      if (product.category && !names.includes(product.category.name)) {
        names.push(product.category.name);
        result.push(<Category>{ Id: product.category.id, Name: product.category.name });
      }
    });

    return result;
  }

  onSelectedCatgoriesChange(selectedCategories: Number[]) {
    this.selectedCategories = selectedCategories;
    this.applyFilters();
  }

  /**
   * Handler for textFilter event of filterBar
   * @param text 
   */
  onFilterTextChange(text: String) {
    this.filterText = text;
    this.applyFilters();
  }

  private listForMobile() {
    this.filteredProducts = this.allProducts.slice(0, 8);
    this.hasNext = true;
    if (this.selectedCategories.length > 0) {
      this.filteredProducts = this.filteredProducts.filter(p => this.selectedCategories.includes(p["category"]["id"]));
    }

    if (this.filterText != "") {
      this.filteredProducts = this.filteredProducts.filter(p => this.accentsUtils.removeAccents(p["name"].toUpperCase()).includes(this.filterText.toUpperCase()));
    }
  }

  private applyFilters() {
    this.filteredProducts = this.allProducts;
    if (this.selectedCategories.length > 0) {
      this.filteredProducts = this.filteredProducts.filter(p => this.selectedCategories.includes(p["category"]["id"]));
    }

    if (this.filterText != "") {
      this.filteredProducts = this.filteredProducts.filter(p => this.accentsUtils.removeAccents(p["name"].toUpperCase()).includes(this.filterText.toUpperCase()));
    }
  }

}